import React, { memo } from "react";
import { useSelector } from "react-redux";
import { Tooltip } from "react-tooltip";
import DataDisplay from "../../../components/dataDisplay/dataDisplay";
import { DetailsModal } from "./detailsModal";
import LoadingAnimation from "../../../components/loadingAnimation/loadingAnimation";

const LABEL_TOOLTIPS = {
  Membership: "Customers who attended a class (or service) using a membership package.",
  "First Timers":
    "Customers who attended a class (or service) for the first time; not purchase or visit type specific.",
  "Intro Offer":
    "Customers who attended a class (or service) with a package tagged as an intro offer.",
  Package: "Customers who attended a class (or service) on a package.",
  "Cross Club":
    "Customers who attended a class (or service) in your location, but their original profile resides in another location.",
  ClassPass: "Customers who attended a class (or service) by way of ClassPass.",
  Other:
    "Customers who attended a class (or service) with a credit not identified as a membership or package; example 'Free Session/Not Paid.'",
  "Wait List Completed":
    "Customers were on a wait list then they were moved to a class and completed a visit.",
  "Wait List No Shows":
    "Customers were on a wait list then they were moved to a class and no showed.",
  "Wait List Late Cancel":
    "Customers were on a wait list then they were moved to a class and late cancel."
};

export const DataPoints = memo(function DataPoints() {
  const dataPoints = useSelector(
    (state) => state.utilization.dataPoints.utilization_by_category ?? []
  );
  const generalData = useSelector((state) => state.utilization.dataPoints.general_data);
  const isDataPointsLoading = useSelector((state) => state.utilization.isDataPointsLoading);
  const showAverages = useSelector((state) => state.utilization.showAverages);
  const isUserQa = useSelector((state) => state.commonReducer.commonProps.qa);
  const symbol = useSelector((state) => state.utilization.chartCurrencySymbol);

  if (isDataPointsLoading) return <LoadingAnimation height={200} />;
  if (!dataPoints.length) return null;

  const { label, value, url, subProps, decimal } = dataPoints[0];
  const totalValueDisplayCustomValue = !value && showAverages ? "--" : null;
  let restDataPoints = dataPoints.slice(1);

  const style = {
    flexBasis: restDataPoints.length <= 5 ? "0" : restDataPoints.length % 2 === 0 ? "32%" : "24%",
    flexGrow: restDataPoints.length <= 5 ? "1" : 0
  };

  return (
    <div className="data-points">
      <div className="data-point-primary-group">
        <div className="data-point">
          <DataDisplay
            label={label}
            value={value}
            disableAnimation={isUserQa}
            currency={!value && showAverages ? "" : symbol}
            customValue={totalValueDisplayCustomValue}
            url={!value && showAverages ? "" : url}
            type="money"
            sectionProps={{ style: { fontSize: "inherit" } }}
            decimal={decimal}
            {...subProps}
          />
        </div>
        {generalData?.map(({ label, value, url, subProps, decimal }) => {
          const generalTooltipContent =
            label === "Avg Number of Days"
              ? "Average number of days customers book classes in advance (Class Date minus Booking Date)"
              : LABEL_TOOLTIPS[label];
          const generalTooltipId = `general-tooltip-${label.toLowerCase().replace(/\s+/g, "-")}`;
          return (
            <div key={label} className="data-point">
              <DataDisplay
                label={
                  generalTooltipContent ? (
                    <>
                      <span
                        data-tooltip-id={generalTooltipId}
                        data-tooltip-content={generalTooltipContent}
                        style={{ cursor: "default", userSelect: "none" }}
                      >
                        {label}
                      </span>
                      <Tooltip
                        id={generalTooltipId}
                        place="top"
                        style={{ textTransform: "none" }}
                      />
                    </>
                  ) : (
                    label
                  )
                }
                value={value}
                disableAnimation={isUserQa}
                currency=""
                url={url}
                type="money"
                decimal={decimal}
                {...subProps}
              />
            </div>
          );
        })}
      </div>
      {restDataPoints.length ? (
        <div className="with-border">
          {restDataPoints.map(({ label, value, url, subProps, decimal }) => {
            const averageDisplayCustomValue = !value && showAverages ? "--" : null;
            const tooltipContent = LABEL_TOOLTIPS[label];
            const tooltipId = `label-tooltip-${label.toLowerCase().replace(/\s+/g, "-")}`;
            return (
              <div key={label} className="data-point" style={style}>
                <DataDisplay
                  label={
                    <>
                      <span
                        data-tooltip-id={tooltipContent ? tooltipId : undefined}
                        data-tooltip-content={tooltipContent || undefined}
                        style={
                          tooltipContent ? { cursor: "default", userSelect: "none" } : undefined
                        }
                      >
                        {label}
                      </span>
                      <DetailsModal label={label} />
                      {tooltipContent && (
                        <Tooltip id={tooltipId} place="top" style={{ textTransform: "none" }} />
                      )}
                    </>
                  }
                  size={36}
                  value={value}
                  disableAnimation={isUserQa}
                  currency={!value && showAverages ? "" : symbol}
                  customValue={averageDisplayCustomValue}
                  url={!value && showAverages ? "" : url}
                  type="money"
                  decimal={decimal}
                  {...subProps}
                />
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
});
